'use client';

import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Typography } from '@mui/material';
import {
  EVENT_LEAD_FORM_GRID_SX,
  EventLeadFormSkeleton,
  LeadCaptureForm,
} from '@/components/EventLeadForm';
import MarketingInteriorLayout, {
  MARKETING_FORM_COLUMN_INNER_SX,
  MARKETING_FORM_COLUMN_SX,
  MARKETING_FORM_HERO_COLUMN_SX,
} from '@/components/home-landing/MarketingInteriorLayout';
import {
  FORM_PAGE_DESKTOP_BP,
  heroTextLegibility,
} from '@/components/design-studio/design-studio-glass';

const DEFAULT_SUBTITLE =
  'Share your details so our team can follow up about services, materials, or updates.';

const LOGO_SRC = '/imgs/NIICO-Logo.png';

function EventFormShell({ children }) {
  return (
    <MarketingInteriorLayout heroBackground formGrid formGridSx={EVENT_LEAD_FORM_GRID_SX}>
      {children}
    </MarketingInteriorLayout>
  );
}

function EventCopyColumn({ title, subtitle }) {
  return (
    <Box
      sx={{
        ...MARKETING_FORM_HERO_COLUMN_SX,
        textAlign: { xs: 'center', [FORM_PAGE_DESKTOP_BP]: 'left' },
      }}
    >
      <Typography
        component="h1"
        sx={{
          ...heroTextLegibility,
          fontWeight: 600,
          fontSize: {
            xs: 'clamp(1.75rem, 5vw, 2.5rem)',
            [FORM_PAGE_DESKTOP_BP]: '2.75rem',
            xl: '3.25rem',
          },
          lineHeight: 1.15,
          letterSpacing: '-0.02em',
          mb: { xs: 2, [FORM_PAGE_DESKTOP_BP]: 1.25 },
        }}
      >
        {title}
      </Typography>
      <Typography
        component="p"
        sx={{
          ...heroTextLegibility,
          fontWeight: 400,
          fontSize: { xs: '1rem', [FORM_PAGE_DESKTOP_BP]: '1.0625rem' },
          lineHeight: { xs: 1.65, [FORM_PAGE_DESKTOP_BP]: 1.55 },
          maxWidth: { [FORM_PAGE_DESKTOP_BP]: 420, xl: 480 },
          mx: { xs: 'auto', [FORM_PAGE_DESKTOP_BP]: 0 },
          opacity: 0.95,
        }}
      >
        {subtitle}
      </Typography>
    </Box>
  );
}

function EventFormMessageColumn({ severity, children }) {
  return (
    <>
      <EventCopyColumn title="Event registration" subtitle={DEFAULT_SUBTITLE} />
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          display: 'flex',
          alignItems: { xs: 'stretch', [FORM_PAGE_DESKTOP_BP]: 'center' },
          justifyContent: { xs: 'stretch', [FORM_PAGE_DESKTOP_BP]: 'center' },
        }}
      >
        <Alert
          severity={severity}
          sx={{
            width: '100%',
            maxWidth: { [FORM_PAGE_DESKTOP_BP]: 520, xl: 560 },
            bgcolor: 'rgba(255, 255, 255, 0.15)',
            color: 'common.white',
            border: '1px solid rgba(255, 255, 255, 0.35)',
            backdropFilter: 'blur(12px)',
            '& .MuiAlert-icon': { color: 'common.white' },
          }}
        >
          {children}
        </Alert>
      </Box>
    </>
  );
}

EventFormShell.propTypes = {
  children: PropTypes.node.isRequired,
};

EventCopyColumn.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
};

EventFormMessageColumn.propTypes = {
  severity: PropTypes.oneOf(['error', 'warning', 'info', 'success']).isRequired,
  children: PropTypes.node.isRequired,
};

export default function EventLeadFormPage() {
  const [state, setState] = React.useState({ loading: true, error: null, data: null });

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/t/campaigns/public/lead-form-config/');
        if (cancelled) return;
        if (res.status === 404) {
          setState({
            loading: false,
            error: 'No active event is configured.',
            data: null,
          });
          return;
        }
        if (!res.ok) {
          setState({
            loading: false,
            error: 'Could not load event form.',
            data: null,
          });
          return;
        }
        const data = await res.json();
        setState({ loading: false, error: null, data });
      } catch {
        if (!cancelled) {
          setState({ loading: false, error: 'Network error.', data: null });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.loading) {
    return <EventLeadFormSkeleton />;
  }

  if (state.error) {
    return (
      <EventFormShell>
        <EventFormMessageColumn severity="error">{state.error}</EventFormMessageColumn>
      </EventFormShell>
    );
  }

  const { event_code: eventCode, event_name: eventName, intents } = state.data;

  if (!intents?.length) {
    return (
      <EventFormShell>
        <EventFormMessageColumn severity="warning">
          No intent options are configured for this event. Please contact the organizer.
        </EventFormMessageColumn>
      </EventFormShell>
    );
  }

  return (
    <EventFormShell>
      <EventCopyColumn title={eventName} subtitle={DEFAULT_SUBTITLE} />
      <Box sx={MARKETING_FORM_COLUMN_SX}>
        <Box sx={MARKETING_FORM_COLUMN_INNER_SX}>
          <LeadCaptureForm
            variant="glass"
            eventCode={eventCode}
            eventName={eventName}
            logoSrc={LOGO_SRC}
            intentOptions={intents}
          />
        </Box>
      </Box>
    </EventFormShell>
  );
}
